import styled from "@emotion/styled";
import {
  CheckCircleIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/20/solid";
import {
  CheckCircleIcon as CheckCircleIconOutline,
  DocumentDuplicateIcon as DocumentDuplicateIconOutline,
} from "@heroicons/react/24/outline";
import copyToClipboard from "copy-to-clipboard";
import React, { useCallback, useEffect, useRef, useState } from "react";
import MinimalButton from "./MinimalButton";

const StyledButton = styled(MinimalButton)`
  overflow: hidden;
`;

const IconContainer = styled.div`
  position: relative;
  width: 20px;
  height: 20px;

  svg {
    position: absolute;
    inset: 0;
    transition: 300ms ease-in-out;
  }
`;

const ButtonLabel = styled.div<{ copied: boolean }>`
  max-width: ${(props) => (props.copied ? "52px" : "36px")};
  transition: 300ms ease-in-out;
`;

interface CopyButtonProps extends React.ComponentProps<typeof MinimalButton> {
  includeLabel?: boolean;
  useOutlineIcons?: boolean;
  iconSize?: string;
  value: string;
}

const CopyButton = ({
  includeLabel,
  useOutlineIcons,
  iconSize,
  value,
  onClick,
  ...buttonProps
}: CopyButtonProps) => {
  const [copied, setCopied] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const cleanupTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const onClickCopy = useCallback(
    (event) => {
      copyToClipboard(value);
      cleanupTimeout();
      setCopied(true);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME
      onClick?.(event);

      timeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 3000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [value],
  );

  useEffect(
    () => () => {
      cleanupTimeout();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [],
  );

  return (
    <StyledButton
      {...buttonProps}
      onClick={onClickCopy}
      icon={
        <IconContainer>
          {useOutlineIcons ? (
            <>
              <DocumentDuplicateIconOutline
                style={{
                  opacity: copied ? 0 : 1,
                  height: iconSize ?? "18px",
                  width: iconSize ?? "18px",
                }}
              />
              <CheckCircleIconOutline
                style={{
                  opacity: copied ? 1 : 0,
                  height: iconSize ?? "18px",
                  width: iconSize ?? "18px",
                }}
              />
            </>
          ) : (
            <>
              <DocumentDuplicateIcon
                style={{
                  opacity: copied ? 0 : 1,
                  height: iconSize ?? "18px",
                  width: iconSize ?? "18px",
                }}
              />

              <CheckCircleIcon
                style={{
                  opacity: copied ? 1 : 0,
                  height: iconSize ?? "18px",
                  width: iconSize ?? "18px",
                }}
              />
            </>
          )}
        </IconContainer>
      }
    >
      {includeLabel && (
        <ButtonLabel copied={copied}>{copied ? "Copied" : "Copy"}</ButtonLabel>
      )}
    </StyledButton>
  );
};

export default CopyButton;
